/* eslint-disable camelcase */
import { Module } from 'vuex'
import { RootState } from '../types'
import api from '@/api/axios'

export interface Invoice {
  id: number
  invoice_number: string
  tenant_id: number
  tenant_name?: string
  unit_name?: string
  property_name?: string
  amount: number
  due_date: string
  status: 'paid' | 'unpaid' | 'overdue'
}

export interface InvoicesState {
  list: Invoice[]
  loading: boolean
}

const invoices: Module<InvoicesState, RootState> = {
  namespaced: true,

  state: (): InvoicesState => ({
    list: [],
    loading: false
  }),

  getters: {
    all: (state) => state.list,
    loading: (state) => state.loading,
    overdue: (state) => state.list.filter(inv => {
      if (inv.status === 'paid') return false
      return new Date(inv.due_date) < new Date()
    })
  },

  mutations: {
    SET_LIST (state, list: Invoice[]) { state.list = list },
    SET_LOADING (state, val: boolean) { state.loading = val },
    ADD (state, invoice: Invoice) { state.list.unshift(invoice) },
    UPDATE (state, updated: Invoice) {
      const i = state.list.findIndex(inv => inv.id === updated.id)
      if (i !== -1) state.list.splice(i, 1, updated)
    },
    REMOVE (state, id: number) {
      state.list = state.list.filter(inv => inv.id !== id)
    },
    MARK_PAID (state, id: number) {
      const inv = state.list.find(i => i.id === id)
      if (inv) inv.status = 'paid'
    }
  },

  actions: {
    async fetchAll ({ commit }, params?: { status?: string; month?: string }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/invoices', { params })
        commit('SET_LIST', data.data ?? data)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async create ({ commit }, payload: Partial<Invoice>) {
      const { data } = await api.post('/invoices', payload)
      commit('ADD', data.data ?? data)
      return data
    },

    async markPaid ({ commit }, { id, payment_method, payment_date }: { id: number; payment_method: string; payment_date: string }) {
      const { data } = await api.post(`/invoices/${id}/mark-paid`, { payment_method, payment_date })
      commit('MARK_PAID', id)
      return data
    },

    async remove ({ commit }, id: number) {
      await api.delete(`/invoices/${id}`)
      commit('REMOVE', id)
    }
  }
}

export default invoices
